import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  MapPin,
  Plus,
  QrCode,
  ShieldCheck,
  Smartphone,
  X,
} from "lucide-react";
import { blankFieldVerification } from "../lib/projectStore";
import type {
  FieldVerificationRecord,
  ProjectWorkspace,
} from "../types/domain";

interface FieldVerificationViewProps {
  workspace: ProjectWorkspace;
  verifications: FieldVerificationRecord[];
  onSave: (record: FieldVerificationRecord) => void;
  onRemove: (id: string) => void;
}

export function FieldVerificationView({
  workspace,
  verifications,
  onSave,
  onRemove,
}: FieldVerificationViewProps) {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const tokenRecord = verifications.find((item) => item.token === token);
  const [draft, setDraft] = useState<FieldVerificationRecord | null>(
    tokenRecord ? { ...tokenRecord } : null,
  );
  const [qrRecord, setQrRecord] = useState<FieldVerificationRecord | null>(null);
  const [qrUrl, setQrUrl] = useState("");

  const entities = useMemo(
    () => [
      ...workspace.criticalControls.map((item) => ({
        type: "control" as const,
        id: item.id,
        label: `Control · ${item.title || "Untitled"}`,
      })),
      ...workspace.risks.map((item) => ({
        type: "risk" as const,
        id: item.id,
        label: `Risk · ${item.designElement || item.hazard || "Untitled"}`,
      })),
      ...workspace.hirarcAssessments.map((item) => ({
        type: "hirarc" as const,
        id: item.id,
        label: `HIRARC · ${item.activity || item.hazard || "Untitled"}`,
      })),
      ...workspace.documentRevisions.map((item) => ({
        type: "document" as const,
        id: item.id,
        label: `Document · ${item.documentNumber} ${item.revision}`,
      })),
      ...workspace.evidence.map((item) => ({
        type: "evidence" as const,
        id: item.id,
        label: `Evidence · ${item.title || "Untitled"}`,
      })),
    ],
    [workspace],
  );

  useEffect(() => {
    if (!qrRecord) {
      setQrUrl("");
      return;
    }
    let active = true;
    const url = `${window.location.origin}/field?token=${encodeURIComponent(qrRecord.token)}`;
    void import("qrcode").then(({ default: QRCode }) =>
      QRCode.toDataURL(url, {
        errorCorrectionLevel: "H",
        width: 360,
        margin: 2,
        color: { dark: "#062a26", light: "#ffffff" },
      }).then((result) => {
        if (active) setQrUrl(result);
      }),
    );
    return () => {
      active = false;
    };
  }, [qrRecord]);

  const saveDraft = () => {
    if (!draft) return;
    const now = new Date().toISOString();
    const expired = Boolean(draft.expiresAt && draft.expiresAt < now);
    onSave({
      ...draft,
      status: expired
        ? "expired"
        : draft.verifiedBy && draft.observation
          ? draft.status === "failed"
            ? "failed"
            : "verified"
          : "open",
      verifiedAt:
        draft.verifiedBy && draft.observation ? draft.verifiedAt || now : "",
    });
    setDraft(null);
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker">
            <Smartphone size={17} /> Mobile point-of-work assurance
          </span>
          <h2>Take the design decision to the person verifying the work.</h2>
          <p>
            Issue a project-scoped QR challenge for a risk, control, HIRARC, revision
            or evidence item. The verifier signs in, records the observation and
            returns the result to the same audit trail.
          </p>
        </div>
        <button
          className="button button-primary"
          onClick={() => setDraft(blankFieldVerification())}
        >
          <Plus size={17} /> Create verification
        </button>
      </section>

      {tokenRecord && (
        <section className="field-mode-banner">
          <ShieldCheck size={22} />
          <div>
            <strong>Field verification opened from QR</strong>
            <p>{tokenRecord.title}</p>
          </div>
          <button className="button button-primary" onClick={() => setDraft({ ...tokenRecord })}>
            Complete verification
          </button>
        </section>
      )}

      <section className="verification-register">
        {verifications.length === 0 ? (
          <div className="empty-state">
            <QrCode size={34} />
            <h3>No point-of-work verifications</h3>
            <p>Create a QR record for installation checks, pre-starts, inspections or handover.</p>
          </div>
        ) : (
          verifications.map((record) => (
            <article key={record.id}>
              <div className={`verification-icon verification-${record.status}`}>
                {record.status === "verified" ? <CheckCircle2 size={20} /> : <QrCode size={20} />}
              </div>
              <div>
                <span className="eyebrow">{record.verificationType}</span>
                <h3>{record.title || "Untitled field verification"}</h3>
                <p>
                  {record.assignedTo || "Unassigned"} · expires{" "}
                  {record.expiresAt ? new Date(record.expiresAt).toLocaleString() : "not set"}
                </p>
                {record.observation && (
                  <blockquote>{record.observation}</blockquote>
                )}
              </div>
              <div className="verification-actions">
                <em className={`status-${record.status}`}>{record.status}</em>
                <button className="button-link" onClick={() => setQrRecord(record)}>
                  Show QR
                </button>
                <button className="button-link" onClick={() => setDraft({ ...record })}>
                  Open
                </button>
                {record.status === "open" && (
                  <button className="button-link danger-text" onClick={() => onRemove(record.id)}>
                    Remove
                  </button>
                )}
              </div>
            </article>
          ))
        )}
      </section>

      {qrRecord && (
        <div className="modal-backdrop">
          <section className="modal-card qr-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Secure sign-in required</span>
                <h2>{qrRecord.title}</h2>
              </div>
              <button className="icon-button" onClick={() => setQrRecord(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="qr-content">
              {qrUrl ? <img src={qrUrl} alt={`QR verification for ${qrRecord.title}`} /> : <QrCode size={180} />}
              <p>
                Scan with a mobile device. The verifier must be an authenticated member
                of this organisation and the record expires at the stated time.
              </p>
              <code>{qrRecord.token}</code>
            </div>
          </section>
        </div>
      )}

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Point-of-work record</span>
                <h2>{draft.id ? "Field verification" : "New verification"}</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <label className="field wide">
                  <span>Verification title</span>
                  <input
                    value={draft.title}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>Linked assurance item</span>
                  <select
                    value={`${draft.entityType}:${draft.entityId}`}
                    onChange={(event) => {
                      const [entityType, entityId] = event.target.value.split(":");
                      const entity = entities.find((item) => item.id === entityId);
                      setDraft({
                        ...draft,
                        entityType: entityType as FieldVerificationRecord["entityType"],
                        entityId,
                        title: draft.title || entity?.label || "",
                      });
                    }}
                  >
                    <option value={`${draft.entityType}:${draft.entityId}`}>Select item</option>
                    {entities.map((item) => (
                      <option key={`${item.type}-${item.id}`} value={`${item.type}:${item.id}`}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Verification type</span>
                  <select
                    value={draft.verificationType}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        verificationType:
                          event.target.value as FieldVerificationRecord["verificationType"],
                      })
                    }
                  >
                    <option value="point-of-work">Point of work</option>
                    <option value="pre-start">Pre-start</option>
                    <option value="installation">Installation</option>
                    <option value="inspection">Inspection</option>
                    <option value="handover">Handover</option>
                  </select>
                </label>
                <label className="field">
                  <span>Assigned verifier</span>
                  <input
                    value={draft.assignedTo}
                    onChange={(event) => setDraft({ ...draft, assignedTo: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>Expiry date and time</span>
                  <input
                    type="datetime-local"
                    value={draft.expiresAt.slice(0, 16)}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        expiresAt: event.target.value
                          ? new Date(event.target.value).toISOString()
                          : "",
                      })
                    }
                  />
                </label>
                <label className="field">
                  <span>Outcome</span>
                  <select
                    value={draft.status}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        status: event.target.value as FieldVerificationRecord["status"],
                      })
                    }
                  >
                    <option value="open">Open</option>
                    <option value="verified">Verified</option>
                    <option value="failed">Failed</option>
                  </select>
                </label>
                <label className="field">
                  <span>Verified by</span>
                  <input
                    value={draft.verifiedBy}
                    onChange={(event) => setDraft({ ...draft, verifiedBy: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>
                    <MapPin size={14} /> Location
                  </span>
                  <input
                    value={draft.location}
                    onChange={(event) => setDraft({ ...draft, location: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>Observation</span>
                  <textarea
                    rows={4}
                    value={draft.observation}
                    onChange={(event) => setDraft({ ...draft, observation: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>Evidence or photograph reference</span>
                  <input
                    value={draft.evidenceReference}
                    onChange={(event) =>
                      setDraft({ ...draft, evidenceReference: event.target.value })
                    }
                  />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="button button-secondary" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button
                className="button button-primary"
                disabled={!draft.title || !draft.entityId || !draft.assignedTo}
                onClick={saveDraft}
              >
                Save verification
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
